import { createSlice } from '@reduxjs/toolkit';
import type {PayloadAction} from "@reduxjs/toolkit";
import { Catalog, Catalogs, IPartsCatalogSceneObject, PartMetadata, PartsCatalogState } from '../../interfaces/PartsCatalog';
import { defaultPartMetadataItem } from '../../components/partsCatalog/defaults';
import { getUuidv4 } from '../../classes/utilities/Misc';

const initialState: PartsCatalogState = {
	catalogs: {},
	simulationHiddenObjects: {},
	simulationHighlightedObjects: {},
	simulationIsolatedObjects: {},
	selectedPart : {},
	highlightedPartId: '',
	hoveredPartId: '',
	showGhostedUnselected : false,
	showCollapseAll: false,
	simulationExpandedObjects: [],
}

const PartsCatalogSlice = createSlice({
	name: "partsCatalog",
	initialState : initialState,
	reducers: {

		loadCatalogs(state, action: PayloadAction<Catalogs>)
		{
			if(action.payload === undefined) return;
			for(const catalog of Object.values(action.payload))
			{
				for(const metadataItem of catalog.partData.metadata)
				{
					metadataItem.id = getUuidv4();
				}
			}
			state.catalogs = action.payload;
		},
		setCatalogForStep(state, action: PayloadAction<{stepId: string, catalog: Catalog}>) 
		{
			state.catalogs[action.payload.stepId] = action.payload.catalog;
		},
		setCatalogEnabled(state, action: PayloadAction<{stepId: string, value: boolean}>)
		{
			state.catalogs[action.payload.stepId].enabled = action.payload.value;
		},
		setCatalogAutoOpen(state, action: PayloadAction<{stepId: string, value: boolean}>)
		{
			state.catalogs[action.payload.stepId].autoOpen = action.payload.value;
		},
		setCatalogTitle(state, action: PayloadAction<{stepId: string, value: string}>)
		{
			state.catalogs[action.payload.stepId].title = action.payload.value;
		},
		setCatalogHighlightColor(state, action: PayloadAction<{stepId: string, value: string}>)
		{
			state.catalogs[action.payload.stepId].highlightColor = action.payload.value;
		},
		setCatalogPartDisplayName(state, action: PayloadAction<{stepId: string, value: string}>)
		{
			state.catalogs[action.payload.stepId].partData.displayName = action.payload.value;
		},
		addPartMetadataItem(state, action: PayloadAction<{stepId: string, item: PartMetadata | undefined}>)
		{
			if(!action.payload.item)
			{
				const index = state.catalogs[action.payload.stepId].partData.metadata.length + 1; //for naming purposes
				const newMetadataItem = {...defaultPartMetadataItem}
				newMetadataItem.name += index;
				newMetadataItem.id = getUuidv4();
				action.payload.item = newMetadataItem;
			}
			state.catalogs[action.payload.stepId].partData.metadata.push(action.payload.item!);
		},
		removePartMetadataItem(state, action: PayloadAction<{stepId: string, index: number}>)
		{
			state.catalogs[action.payload.stepId].partData.metadata.splice(action.payload.index, 1);
		},
		movePartMetadataItem(state, action: PayloadAction<{stepId: string, fromIndex: number, toIndex: number}>)
		{
			state.catalogs[action.payload.stepId].partData.metadata.splice(action.payload.toIndex, 0, state.catalogs[action.payload.stepId].partData.metadata.splice(action.payload.fromIndex, 1)[0]);
		},
		setPartMetadataItemName(state, action: PayloadAction<{stepId: string, index: number, value: string}>)
		{
			state.catalogs[action.payload.stepId].partData.metadata[action.payload.index].name = action.payload.value;
		},
		setPartMetadataItemData(state, action: PayloadAction<{stepId: string, index: number, value: string}>)
		{
			state.catalogs[action.payload.stepId].partData.metadata[action.payload.index].data = action.payload.value;
		},
		clearSimulationObjects(state)
		{
			state.simulationHiddenObjects = {};
			state.simulationHighlightedObjects = {};
			state.simulationIsolatedObjects = {};
			state.selectedPart = {};	
			state.showGhostedUnselected = false;
		},
		addSimulationHiddenObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationHiddenObjects[action.payload.stepId])
			{
				state.simulationHiddenObjects[action.payload.stepId] = [];
			}
			state.simulationHiddenObjects[action.payload.stepId].push(action.payload.partId);
		},
		removeSimulationHiddenObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationHiddenObjects[action.payload.stepId])
			{
				state.simulationHiddenObjects[action.payload.stepId] = [];
				return;
			}
			state.simulationHiddenObjects[action.payload.stepId] = state.simulationHiddenObjects[action.payload.stepId].filter(obj => obj !== action.payload.partId);
		},
		addSimulationHighlightedObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationHighlightedObjects[action.payload.stepId])
			{
				state.simulationHighlightedObjects[action.payload.stepId] = [];
			}
			state.simulationHighlightedObjects[action.payload.stepId].push(action.payload.partId);
		},
		removeSimulationHighlightedObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationHighlightedObjects[action.payload.stepId])
			{
				state.simulationHighlightedObjects[action.payload.stepId] = [];
				return;
			}
			state.simulationHighlightedObjects[action.payload.stepId] = state.simulationHighlightedObjects[action.payload.stepId].filter(obj => obj !== action.payload.partId);
		},
		addSimulationIsolatedObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationIsolatedObjects[action.payload.stepId])
			{
				state.simulationIsolatedObjects[action.payload.stepId] = [];
			}
			state.simulationIsolatedObjects[action.payload.stepId].push(action.payload.partId);
		},
		removeSimulationIsolatedObject(state, action: PayloadAction<{stepId: string, partId: string}>)
		{
			if(!state.simulationIsolatedObjects[action.payload.stepId])
			{
				state.simulationIsolatedObjects[action.payload.stepId] = [];
				return;
			}
			state.simulationIsolatedObjects[action.payload.stepId] = state.simulationIsolatedObjects[action.payload.stepId].filter(obj => obj !== action.payload.partId);
		},
		addSimulationExpandedObject(state, action: PayloadAction<string>)
		{
			state.simulationExpandedObjects.push(action.payload);
		},
		removeSimulationExpandedObject(state, action: PayloadAction<string>)
		{
			state.simulationExpandedObjects = state.simulationExpandedObjects.filter(obj => obj !== action.payload);
		},
		clearSimulationExpandedObjects(state)
		{
			state.simulationExpandedObjects = [];
		},
		setSelectedPart(state, action: PayloadAction<{stepId: string, part: IPartsCatalogSceneObject | null}>)
		{
			if(!state.selectedPart[action.payload.stepId])
			{
				state.selectedPart[action.payload.stepId] = null;
			}
			state.selectedPart[action.payload.stepId] = action.payload.part;
		},
		toggleShowGhostedUnselected(state)
		{
			state.showGhostedUnselected = !state.showGhostedUnselected;
		},
		setShowCollapseAll(state, action: PayloadAction<boolean>)
		{
			state.showCollapseAll = action.payload;
		},
		setHighlightedPartId(state, action: PayloadAction<string>)
		{
			state.highlightedPartId = action.payload;
		},
		setHoveredPartId(state, action: PayloadAction<string>)
		{
			state.hoveredPartId = action.payload;
		},
	},
});

export default PartsCatalogSlice;
